import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Repeat, Tag } from "lucide-react";
import { Img } from "@/components/common/Primitives";
import { SIZES } from "@/lib/images";
import { useLang } from "@/lib/i18n";
import { fmtDate } from "@/lib/format";

/**
 * Event poster card: consistent 3:4 thumbnail, title, date/price meta and a CTA.
 * `item` comes from /content (happenings[]): { id, title, when, text, date, time, price, image }
 */
export const PosterCard = ({ item, testid = "event-poster-card", to, compact = false, eager = false }) => {
  const { lang, tx, tr } = useLang();
  const href = to || `/arrangement?event=${item.id}#det-skjer`;
  const dateLabel = item.date ? fmtDate(item.date, lang, lang === "en" ? "EEE d MMM" : "EEE d. MMM") : tx("Fast", "Recurring");
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-[22px] border border-border bg-card transition-[border-color,transform,box-shadow] duration-300 ease-premium hover:-translate-y-[2px] hover:border-foreground/15 hover:shadow-float" data-testid={testid}>
      <Link to={href} className="block focus-visible:outline-none" aria-label={tr(item.title)} tabIndex={-1}>
        <Img src={item.image} alt={tr(item.title)} className="img-zoom aspect-[3/4]" radius="rounded-none" sizes={SIZES.quarter} eager={eager} position="50% 30%" />
      </Link>
      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium text-muted-foreground">
          <span className="inline-flex items-center gap-1.5 tabular">{item.date ? <CalendarDays className="h-3.5 w-3.5" /> : <Repeat className="h-3.5 w-3.5" />} {dateLabel}{item.time && item.date ? ` · ${item.time}` : ""}</span>
          {item.price && <span className="inline-flex items-center gap-1.5 tnum"><Tag className="h-3.5 w-3.5" /> {tr(item.price)}</span>}
        </div>
        <h3 className={`mt-2 font-semibold leading-snug tracking-[-0.015em] text-foreground ${compact ? "text-[0.9375rem]" : "text-[1.0625rem]"}`}>{tr(item.title)}</h3>
        {!compact && <p className="mt-1.5 line-clamp-3 flex-1 text-sm leading-relaxed text-muted-foreground">{tr(item.text)}</p>}
        <Link to={href} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground underline decoration-border underline-offset-[6px] transition-colors group-hover:decoration-foreground" data-testid={`${testid}-cta`}>
          {tx("Les meir", "Read more")} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </article>
  );
};
